import React from 'react'

import { ActivityLevelValue, EnergyGoal, UserProfile } from '@/api/types'
import { Grid } from '@/components/Grid'
import { Input } from '@/components/Input'
import { Label } from '@/components/Label'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { Switch } from '@/components/Switch'
import { TextField } from '@/components/TextField'
import { useTranslation } from 'react-i18next'
import { EXPERTISE_LEVELS, ExpertiseLevel } from '../recipes/constants'
import { ageToBirthdate, birthdateToAge } from './utils'

const GENDERS = ['M', 'F'] as const
const ACTIVITY_LEVELS = ['LOWEST', 'LOW', 'MEDIUM'] as const
const CALORIC_GOALS = ['MAINTAIN_WEIGHT', 'LOSE_WEIGHT', 'GAIN_WEIGHT', 'CUSTOM'] as const

type Props = {
  userProfile: UserProfile
  onChangeProfile?: (profile: UserProfile) => void
  isHouseholdProfile?: boolean
  isReadOnly?: boolean
}

export default function ProfileEditor({
  userProfile,
  onChangeProfile,
  isHouseholdProfile = false,
  isReadOnly = false,
}: Props) {
  const { t } = useTranslation()
  const isCustomTarget = userProfile.userEnergyDistribution?.energyGoal === 'CUSTOM'
  const isCustomActivityLevel =
    userProfile.activityLevels.length > 0 && userProfile.activityLevels[0].value === 'AUTO'

  function setUserProfile(profile: UserProfile) {
    !isReadOnly && onChangeProfile?.(profile)
  }

  const onChangeAge = (age: string) => {
    setUserProfile({
      ...userProfile,
      birthDate: ageToBirthdate(parseInt(age)),
    })
  }

  const onChangeWeight = (weight: string) => {
    setUserProfile({
      ...userProfile,
      weightLogs: [
        {
          weightKg: parseFloat(weight),
        },
      ],
    })
  }

  const onChangeHeight = (height: string) => {
    setUserProfile({
      ...userProfile,
      heightLogs: [
        {
          heightCm: parseInt(height),
        },
      ],
    })
  }

  const onChangeActivityLevel = (value: ActivityLevelValue) => {
    setUserProfile({
      ...userProfile,
      activityLevels: [
        {
          value: value,
        },
      ],
    })
  }

  function handleChangeCustomActivityLevel(value: boolean) {
    const activityLevel = value ? 'AUTO' : 'LOWEST'
    const dailyBurnedEnergyKcal = activityLevel === 'AUTO' ? 500 : undefined

    setUserProfile({
      ...userProfile,
      activityLevels: [
        {
          value: activityLevel,
          dailyBurnedEnergyKcal,
        },
      ],
    })
  }

  function handleChangeInput(key: string, value: any) {
    setUserProfile({
      ...userProfile,
      [key]: value,
    })
  }

  function handleCustomTargetKcalChange(value: string): void {
    const userEnergyDistribution = userProfile.userEnergyDistribution || {}

    setUserProfile({
      ...userProfile,
      userEnergyDistribution: {
        ...userEnergyDistribution,
        energyGoal: 'CUSTOM',
        customKcalTarget: parseInt(value),
      },
    })
  }

  function handleCustomBurnedCaloriesChange(value: string): void {
    setUserProfile({
      ...userProfile,
      activityLevels: [
        {
          value: 'AUTO',
          dailyBurnedEnergyKcal: parseInt(value),
        },
      ],
    })
  }

  function onCaloricGoalChange(value: EnergyGoal): void {
    const userEnergyDistribution = userProfile.userEnergyDistribution || {}

    const customKcalTarget =
      value === 'CUSTOM' ? (userEnergyDistribution.customKcalTarget ?? 2000) : undefined

    setUserProfile({
      ...userProfile,
      userEnergyDistribution: {
        ...userEnergyDistribution,
        energyGoal: value,
        customKcalTarget,
      },
    })
  }

  return (
    <>
      <Grid columns="1fr 1fr" gap={16}>
        {isHouseholdProfile && (
          <TextField
            isRequired={true}
            isReadOnly={isReadOnly}
            style={{ gridColumn: '1 / span 2' }}
            label={t('profile.name')}
            value={userProfile.firstName}
            errorMessage={t('profile.errorNameEmpty')}
            onChange={(name) => handleChangeInput('firstName', name)}
          />
        )}
        <TextField
          isRequired={true}
          isReadOnly={isReadOnly}
          style={{ gridColumn: '1 / span 2' }}
          label={t('profile.age')}
          type="number"
          value={birthdateToAge(userProfile.birthDate) + ''}
          errorMessage={t('profile.errorAgeEmpty')}
          onChange={onChangeAge}
        />
        <TextField
          isRequired={true}
          isReadOnly={isReadOnly}
          label={t('profile.weight')}
          type="number"
          value={
            (userProfile.weightLogs.length > 0
              ? userProfile.weightLogs[userProfile.weightLogs.length - 1].weightKg
              : 70) + ''
          }
          errorMessage={t('profile.errorWeightEmpty')}
          onChange={onChangeWeight}
        />
        <TextField
          isRequired={true}
          isReadOnly={isReadOnly}
          label={t('profile.height')}
          type="number"
          value={
            (userProfile.heightLogs.length > 0
              ? userProfile.heightLogs[userProfile.heightLogs.length - 1].heightCm
              : 170) + ''
          }
          errorMessage={t('profile.errorHeightEmpty')}
          onChange={onChangeHeight}
        />
        <div style={{ gridColumn: '1 / span 2' }}>
          <Label>{t('profile.gender')}</Label>
          <RadioGroup
            isReadOnly={isReadOnly}
            variation="segmented"
            value={userProfile.gender}
            onChange={(value) => handleChangeInput('gender', value)}>
            {GENDERS.map((tag: (typeof GENDERS)[number]) => (
              <Radio key={tag} value={tag}>
                {t(`profile.genderOptions.${tag}`)}
              </Radio>
            ))}
          </RadioGroup>
        </div>

        {!isHouseholdProfile && (
          <div>
            <Label>{t('profile.energyGoal')}</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <RadioGroup
                isReadOnly={isReadOnly}
                variation="segmented"
                value={userProfile.userEnergyDistribution?.energyGoal ?? 'MAINTAIN_WEIGHT'}
                onChange={onCaloricGoalChange}>
                {CALORIC_GOALS.map((tag: (typeof CALORIC_GOALS)[number]) => (
                  <Radio key={tag} value={tag}>
                    {t(`profile.energyGoalOptions.${tag}`)}
                  </Radio>
                ))}
              </RadioGroup>
              {isCustomTarget && (
                <div>
                  <Input
                    readOnly={isReadOnly}
                    type="number"
                    placeholder="daily kcal"
                    value={userProfile.userEnergyDistribution?.customKcalTarget}
                    onChange={(event) => handleCustomTargetKcalChange(event.target.value)}></Input>
                  {t('profile.kcal')}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <Label>{t('profile.activityLevel')}</Label>

          {isCustomActivityLevel ? (
            <div>
              <Input
                readOnly={isReadOnly}
                type="number"
                disabled={isCustomTarget}
                placeholder="daily burned kcal"
                value={userProfile.userEnergyDistribution?.customKcalTarget}
                onChange={(event) => handleCustomBurnedCaloriesChange(event.target.value)}></Input>
              {t('profile.burnedKcal')}
            </div>
          ) : (
            <RadioGroup
              style={{ marginBottom: 4 }}
              isReadOnly={isReadOnly}
              variation="segmented"
              isDisabled={isCustomTarget}
              value={
                userProfile.activityLevels.length > 0
                  ? userProfile.activityLevels[0].value
                  : 'LOWEST'
              }
              onChange={onChangeActivityLevel}>
              {ACTIVITY_LEVELS.map((tag: (typeof ACTIVITY_LEVELS)[number]) => (
                <Radio key={tag} value={tag}>
                  {t(`profile.activityLevelOptions.${tag}`)}
                </Radio>
              ))}
            </RadioGroup>
          )}
          <Switch
            isReadOnly={isReadOnly}
            isSelected={isCustomActivityLevel}
            onChange={handleChangeCustomActivityLevel}
            isDisabled={isCustomTarget}>
            {t('profile.customActivityLevel')}
          </Switch>
        </div>
      </Grid>

      {!isHouseholdProfile && (
        <div>
          <Label>{t('profile.expertise')}</Label>
          <RadioGroup
            isReadOnly={isReadOnly}
            variation="segmented"
            value={userProfile.expertiseLevel}
            onChange={(value) => handleChangeInput('expertiseLevel', value)}>
            {EXPERTISE_LEVELS.map((tag: ExpertiseLevel) => (
              <Radio key={tag} value={tag}>
                {t(`recipe.expertiseOptions.${tag}`)}
              </Radio>
            ))}
          </RadioGroup>
        </div>
      )}
    </>
  )
}
